// Global Instructions Rule Applied!

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Button, Input, Popconfirm, Space, Table, Tag, Tooltip, Typography } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import type { DistributionListGridModel } from '../../../state_manager/data_models/distribution_list_grid_model.ts'
import { useDistributionLists } from '../hooks/useDistributionLists.ts'
import { deleteDistributionList } from '../services/distributionListsService.ts'

const { Title } = Typography
const DL_MODULE = 'distribution-lists'

const tagList = (csv: string, color: string) => {
  const labels = (csv || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
  if (labels.length === 0) return <span style={{ color: '#9ca3af' }}>—</span>
  return (
    <Space size={[4, 4]} wrap>
      {labels.map((l) => (
        <Tag key={l} color={color}>
          {l}
        </Tag>
      ))}
    </Space>
  )
}

export default function DistributionListsPage() {
  const {
    listsData,
    listsLoading,
    activeSorting,
    handleSetFilters,
    handleSetSorting,
    refetch
  } = useDistributionLists()
  const { openFormInTab } = useTabManagerContext()
  const location = useLocation()

  const [deletingId, setDeletingId] = useState<string | null>(null)

  const currentSort = activeSorting?.[0]

  // The screen is screen-local and the page component stays mounted behind
  // the form tab (display:none), so it isn't remounted on return. Refetch
  // when the URL transitions back to the grid root (post create/edit/cancel)
  // so the grid reflects DB truth.
  const prevPathRef = useRef(location.pathname)
  useEffect(() => {
    const prev = prevPathRef.current
    prevPathRef.current = location.pathname
    if (location.pathname === `/${DL_MODULE}` && prev !== location.pathname) {
      refetch()
    }
  }, [location.pathname, refetch])

  const openCreate = useCallback(() => {
    openFormInTab(DL_MODULE, null)
  }, [openFormInTab])

  const openEdit = useCallback(
    (list: DistributionListGridModel) => {
      openFormInTab(DL_MODULE, list.distribution_lists__id, list.distribution_lists__name)
    },
    [openFormInTab]
  )

  const handleDelete = useCallback(
    async (list: DistributionListGridModel) => {
      setDeletingId(list.distribution_lists__id)
      try {
        const token = getAccessToken(store.getState().auth)
        const result = await deleteDistributionList(list.distribution_lists__id, token)
        if (!result.success) {
          showErrorToast(result.error || 'Failed to delete distribution list')
          return
        }
        toast.success('Distribution list deleted')
        await refetch()
      } finally {
        setDeletingId(null)
      }
    },
    [refetch]
  )

  const columns: ColumnsType<DistributionListGridModel> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'distribution_lists__name',
        key: 'distribution_lists__name',
        sorter: true,
        sortOrder:
          currentSort?.key === 'distribution_lists__name'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (name: string, record: DistributionListGridModel) => (
          <a onClick={() => openEdit(record)} className='font-medium'>
            {name}
          </a>
        )
      },
      {
        title: 'Topic',
        dataIndex: 'distribution_lists__topic_label',
        key: 'distribution_lists__topic_label',
        width: 160,
        sorter: true,
        sortOrder:
          currentSort?.key === 'distribution_lists__topic_label'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (label: string) =>
          label ? <Tag color='geekblue'>{label}</Tag> : <span style={{ color: '#9ca3af' }}>—</span>
      },
      {
        title: 'Account Roles',
        dataIndex: 'distribution_lists__role_labels',
        key: 'distribution_lists__role_labels',
        width: 200,
        render: (_: unknown, record: DistributionListGridModel) =>
          tagList(record.distribution_lists__role_labels, 'blue')
      },
      {
        title: 'Funds',
        dataIndex: 'distribution_lists__fund_labels',
        key: 'distribution_lists__fund_labels',
        width: 320,
        render: (_: unknown, record: DistributionListGridModel) =>
          tagList(record.distribution_lists__fund_labels, 'green')
      },
      {
        title: 'LP Statuses',
        dataIndex: 'distribution_lists__lp_status_labels',
        key: 'distribution_lists__lp_status_labels',
        width: 180,
        render: (_: unknown, record: DistributionListGridModel) =>
          tagList(record.distribution_lists__lp_status_labels, 'gold')
      },
      {
        title: 'Fixed Accounts',
        dataIndex: 'distribution_lists__account_count',
        key: 'distribution_lists__account_count',
        width: 120,
        align: 'right',
        sorter: true,
        sortOrder:
          currentSort?.key === 'distribution_lists__account_count'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (n: number) =>
          n > 0 ? n : <span style={{ color: '#9ca3af' }}>—</span>
      },
      {
        title: 'Fixed Contacts',
        dataIndex: 'distribution_lists__contact_count',
        key: 'distribution_lists__contact_count',
        width: 120,
        align: 'right',
        sorter: true,
        sortOrder:
          currentSort?.key === 'distribution_lists__contact_count'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (n: number) =>
          n > 0 ? n : <span style={{ color: '#9ca3af' }}>—</span>
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 110,
        align: 'center',
        render: (_: unknown, record: DistributionListGridModel) => (
          <Space size='small'>
            <Tooltip title='Edit'>
              <Button
                type='text'
                size='small'
                icon={<EditOutlined />}
                onClick={() => openEdit(record)}
              />
            </Tooltip>
            <Popconfirm
              title='Delete this distribution list?'
              description='Its role / fund / LP-status / account / contact selections are removed too.'
              okText='Delete'
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(record)}
            >
              <Tooltip title='Delete'>
                <Button
                  type='text'
                  size='small'
                  danger
                  loading={deletingId === record.distribution_lists__id}
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        )
      }
    ],
    [currentSort, openEdit, handleDelete, deletingId]
  )

  const handleTableChange = useCallback(
    (_pagination: unknown, _filters: unknown, sorter: any) => {
      const s = Array.isArray(sorter) ? sorter[0] : sorter
      if (s && s.order && s.columnKey) {
        handleSetSorting([
          { key: String(s.columnKey), direction: s.order === 'descend' ? 'desc' : 'asc' }
        ])
      } else {
        handleSetSorting([{ key: 'distribution_lists__name', direction: 'asc' }])
      }
    },
    [handleSetSorting]
  )

  const onSearch = useCallback(
    (value: string) => {
      const v = (value || '').trim()
      handleSetFilters(v ? [{ key: 'distribution_lists__name', value: v }] : [])
    },
    [handleSetFilters]
  )

  return (
    <div className='h-full overflow-hidden'>
      <div className='w-full h-full flex flex-col bg-white px-2 sm:px-4 py-2 sm:py-4 overflow-hidden'>
        <div className='flex items-center justify-between mb-3 gap-3 flex-wrap'>
          <Title level={4} style={{ margin: 0 }}>
            Distribution Lists
          </Title>
          <Space>
            <Input.Search
              placeholder='Search distribution lists...'
              allowClear
              onSearch={onSearch}
              style={{ width: 260 }}
            />
            <Button type='primary' icon={<PlusOutlined />} onClick={openCreate}>
              New Distribution List
            </Button>
          </Space>
        </div>

        <div className='flex-1 min-h-0 overflow-auto'>
          <Table<DistributionListGridModel>
            className='distribution-lists-table'
            rowKey='distribution_lists__id'
            columns={columns}
            dataSource={listsData}
            loading={listsLoading}
            onChange={handleTableChange}
            pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
            size='middle'
          />
        </div>
      </div>
    </div>
  )
}
